import posthog from "posthog-js";
import { useEffect, useRef } from "react";
import { useOrganizationTeamProject } from "~/hooks/useOrganizationTeamProject";
import { usePublicEnv } from "~/hooks/usePublicEnv";
import { useRequiredSession } from "~/hooks/useRequiredSession";
import { api } from "~/utils/api";
import Script from "~/utils/compat/next-script";
import { useCrispBubblePolicy } from "~/utils/crispBubblePolicy";
import { pollForGlobal } from "~/utils/pollForGlobal";

export function ExtraFooterComponents() {
  const session = useRequiredSession({ required: false });
  const publicEnv = usePublicEnv();

  // Crisp only ever loads on SaaS. The policy owns the bubble's visibility
  // (hidden unless deliberately opened) for the whole app lifetime, so it
  // mounts here next to the loader rather than inside any single page.
  useCrispBubblePolicy({ enabled: Boolean(publicEnv.data?.IS_SAAS) });

  if (!publicEnv.data?.IS_SAAS) {
    return null;
  }

  return (
    <>
      <Script id="gtm-init" strategy="afterInteractive">
        {`
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;
          j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
          f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','GTM-KJ4S6Z9C');
        `}
      </Script>
      {session.data?.user ? <SignedInExtraFooterComponents /> : null}
    </>
  );
}

function sanitizeForJs(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/'/g, "\\'")
    .replace(/"/g, '\\"')
    .replace(/</g, "\\x3c")
    .replace(/>/g, "\\x3e");
}

export function SignedInExtraFooterComponents() {
  const updateLastLogin = api.user.updateLastLogin.useMutation();

  const session = useRequiredSession();
  const { organization, project } = useOrganizationTeamProject({
    redirectToOnboarding: false,
  });

  const hasTracked = useRef(false);
  const hasUpdatedLastLogin = useRef(false);

  useEffect(() => {
    const email = session.data?.user?.email;
    const name = session.data?.user?.name;
    const companyName = organization?.name;
    if (!email || !companyName || hasTracked.current) return;

    // Reo is defined by GTM's container once it loads — which is now
    // idle-deferred — so it may not exist yet on first run. Poll instead of
    // giving up permanently.
    return pollForGlobal(
      () => (window as any).Reo,
      (reo) => {
        if (!reo?.identify) return;
        reo.identify({
          username: email,
          type: "email",
          firstname: name || "",
          company: companyName,
        });
        hasTracked.current = true;
      },
    );
  }, [session.data?.user?.email, session.data?.user?.name, organization?.name]);

  // Update last login separately from analytics — don't gate on gtag availability
  useEffect(() => {
    if (
      !session.data?.user ||
      !organization ||
      !project ||
      hasUpdatedLastLogin.current
    )
      return;
    if (session.data.user.impersonator) return;

    hasUpdatedLastLogin.current = true;
    void updateLastLogin.mutate({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization?.id, project?.id]);

  useEffect(() => {
    const user = session.data?.user;
    if (!user || !organization || !project || user.impersonator) return;

    // gtag is defined by GTM's container once it loads — which is now
    // idle-deferred — so it may not exist yet on first run. Poll instead of
    // giving up permanently.
    return pollForGlobal(
      () => (window as any).gtag,
      (gtag) => {
        gtag("set", "user_properties", {
          organization_id: organization.id,
          organization_name: organization.name,
          project_id: project.id,
          project_name: project.name,
          environment: process.env.NODE_ENV,
          user_id: user.id,
        });

        gtag("event", "open_dashboard", {
          organization_id: organization.id,
          organization_name: organization.name,
          project_id: project.id,
          project_name: project.name,
          environment: process.env.NODE_ENV,
          user_id: user.id,
        });
      },
    );
  }, [organization?.id, project?.id]);

  useEffect(() => {
    if (session.data?.user && !session.data.user.impersonator) {
      posthog.identify(session.data.user.id, {
        email: session.data.user.email,
        name: session.data.user.name,
        organization_id: organization?.id,
        organization_name: organization?.name,
        project_id: project?.id,
        project_name: project?.name,
      });
    }
  }, [session.data?.user, organization?.id, project?.id]);

  if (!session.data || !organization || !project) {
    return null;
  }

  const isImpersonating = !!session.data?.user?.impersonator;
  const isInStudio =
    typeof window !== "undefined" &&
    window.location.pathname.includes("/studio");

  return (
    <>
      {isImpersonating ? null : (
        <>
          <Script id="pendo">
            {`(function(apiKey){
    (function(p,e,n,d,o){var v,w,x,y,z;o=p[d]=p[d]||{};o._q=o._q||[];
    v=['initialize','identify','updateOptions','pageLoad','track'];for(w=0,x=v.length;w<x;++w)(function(m){
        o[m]=o[m]||function(){o._q[m===v[0]?'unshift':'push']([m].concat([].slice.call(arguments,0)));};})(v[w]);
        y=e.createElement(n);y.async=!0;y.src='https://cdn.eu.pendo.io/agent/static/'+apiKey+'/pendo.js';
        z=e.getElementsByTagName(n)[0];z.parentNode.insertBefore(y,z);})(window,document,'script','pendo');

    pendo.initialize({
        visitor: {
            id: '${sanitizeForJs(session.data.user?.id || "")}',
            email: '${sanitizeForJs(session.data.user?.email || "")}',
            name: '${sanitizeForJs(session.data.user?.name || "")}'
        },
        account: {
            id: '${sanitizeForJs(organization.id || "")}',
            projectName: '${sanitizeForJs(project.name || "")}',
            organizationName: '${sanitizeForJs(organization.name || "")}'
        }
    });
})('18f008fe-1a55-4b22-70d9-964d6e98b130');`}
          </Script>
          {isInStudio ? null : (
            /* Loader only: hide/show behavior lives in the Crisp bubble
               policy (~/utils/crispBubblePolicy), which seeds the queue
               before this runs. `window.$crisp || []` preserves that queue
               so the policy's commands and event bindings drain at boot. */
            <Script id="crisp">
              {`window.$crisp=window.$crisp||[];window.CRISP_WEBSITE_ID="cca9eacd-c4d6-4258-a7fc-9606be6fd012";(function(){d=document;s=d.createElement("script");s.src="https://client.crisp.chat/l.js";s.async=1;d.getElementsByTagName("head")[0].appendChild(s);})();`}
            </Script>
          )}
        </>
      )}
    </>
  );
}
